import { DateTime } from 'luxon';
import {
  getPersonalTrialDayCounts,
  type TrialLicense,
} from './trial-license-day-count';

/** Must stay in sync with getPlanKeyLegacyFreeTier() in apps/core licensing.config. */
const LEGACY_FREE_PLAN_KEY = 'teacher_free';

/**
 * Last moment legacy free users retain full Pro features (TE-2506).
 *
 * Duplicated as `PAYWALL_GRACE_PERIOD_END_ISO` in
 * `apps/core/src/modules/permission/grading-feature-permission.service.ts`,
 * which enforces the same grace window server-side. apps/core imports no
 * `@teachino/*` library, so the two copies must be changed together.
 */
const PAYWALL_GRACE_PERIOD_END_ISO = '2026-07-31T23:59:59.999Z';

/** Start of the grace countdown shown in the navigation bar. */
const PAYWALL_GRACE_PERIOD_START_ISO = '2026-07-01T00:00:00.000Z';

export type PaywallGraceDayCounts = {
  graceDaysLeft: number;
  graceDaysTotal: number;
};

function toDateTime(value: string): DateTime {
  return DateTime.fromISO(value);
}

export function hasPaidLicense(
  licenses: { plan: { type: string } }[],
): boolean {
  return licenses.some(
    (license) =>
      license.plan.type === 'PAID' || license.plan.type === 'FRIENDS',
  );
}

export function findPersonalLegacyFreeLicense<T extends TrialLicense>(
  licenses: T[],
): T | undefined {
  return licenses.find(
    (license) =>
      license.holderType === 'USER' &&
      license.plan.type === 'FREE' &&
      license.plan.key === LEGACY_FREE_PLAN_KEY,
  );
}

export function getPaywallGraceDayCounts(): PaywallGraceDayCounts | null {
  const graceStartAt = toDateTime(PAYWALL_GRACE_PERIOD_START_ISO);
  const graceEndAt = toDateTime(PAYWALL_GRACE_PERIOD_END_ISO);

  if (!graceStartAt.isValid || !graceEndAt.isValid) {
    return null;
  }

  const now = DateTime.now();

  if (now.toMillis() > graceEndAt.toMillis()) {
    return null;
  }

  const graceDaysTotal = Math.ceil(graceEndAt.diff(graceStartAt, 'days').days);
  const graceDaysLeft = Math.max(
    0,
    Math.ceil(graceEndAt.diff(now, 'days').days),
  );

  if (graceDaysTotal <= 0) {
    return null;
  }

  return { graceDaysLeft, graceDaysTotal };
}

export function getPersonalPaywallGraceDayCounts(
  licenses: TrialLicense[],
): PaywallGraceDayCounts | null {
  if (hasPaidLicense(licenses)) {
    return null;
  }

  if (getPersonalTrialDayCounts(licenses) !== null) {
    return null;
  }

  if (!findPersonalLegacyFreeLicense(licenses)) {
    return null;
  }

  return getPaywallGraceDayCounts();
}

/** Legacy {@link LEGACY_FREE_PLAN_KEY} users within the TE-2506 grace window retain full Pro access. */
export function isInLegacyGracePeriod(licenses: TrialLicense[]): boolean {
  if (hasPaidLicense(licenses)) {
    return false;
  }

  if (!findPersonalLegacyFreeLicense(licenses)) {
    return false;
  }

  return getPaywallGraceDayCounts() !== null;
}
